import React, { useState, useEffect } from "react";
import { CalendarPlus, CalendarX, Loader2 } from "lucide-react";

export function AbsenceRequestSection({ token }) {
  const [absences, setAbsences] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ date_from: "", date_to: "", absence_type: "ferie", partial: false, time_from: "", time_to: "", note: "" });
  const [saving, setSaving] = useState(false);
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    fetch(`${API_URL}/api/driver/absences`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json()).then(d => setAbsences(d || [])).catch(() => {});
  }, [token, API_URL]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.date_from || !form.date_to) return;
    setSaving(true);
    try {
      const r = await fetch(`${API_URL}/api/driver/absences`, {
        method: "POST", headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      if (r.ok) {
        const newAbs = await r.json();
        setAbsences([newAbs, ...absences]);
        setShowForm(false);
        setForm({ date_from: "", date_to: "", absence_type: "ferie", partial: false, time_from: "", time_to: "", note: "" });
      }
    } catch { /* ignore */ }
    setSaving(false);
  };

  const statusC = { approvato: "bg-green-500", in_attesa: "bg-amber-500", rifiutato: "bg-red-500" };
  const statusL = { approvato: "Approvato", in_attesa: "In Attesa", rifiutato: "Rifiutato" };
  const typeL = { ferie: "Ferie", malattia: "Malattia", permesso: "Permesso", infortunio: "Infortunio", altro: "Altro" };
  const typeC = { ferie: "text-blue-400", malattia: "text-red-400", permesso: "text-amber-400", infortunio: "text-purple-400", altro: "text-zinc-400" };

  return (
    <div className="flex flex-col h-full" data-testid="driver-absence-section">
      <div className="flex items-center justify-between mb-4">
        <span className="text-sm font-bold uppercase tracking-wider text-zinc-300">Le Mie Assenze</span>
        <button onClick={() => setShowForm(!showForm)} data-testid="driver-request-absence-btn"
          className="flex items-center gap-1.5 px-4 py-2 bg-[#FF5200] text-white text-xs font-bold uppercase rounded-sm hover:bg-[#e64a00]">
          <CalendarPlus size={14} /> {showForm ? "Chiudi" : "Nuova Richiesta"}
        </button>
      </div>

      {showForm && (
        <form onSubmit={submit} className="bg-zinc-800 border border-zinc-700 rounded-sm p-4 mb-4 space-y-3" data-testid="driver-absence-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1">Dal *</label>
              <input type="date" value={form.date_from} onChange={e => setForm({ ...form, date_from: e.target.value, date_to: form.partial ? e.target.value : form.date_to })}
                className="w-full h-10 px-3 bg-zinc-700 border border-zinc-600 rounded-sm text-sm text-white" data-testid="driver-abs-from" />
            </div>
            <div>
              <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1">Al *</label>
              <input type="date" value={form.date_to} onChange={e => setForm({ ...form, date_to: e.target.value })}
                className="w-full h-10 px-3 bg-zinc-700 border border-zinc-600 rounded-sm text-sm text-white" data-testid="driver-abs-to" />
            </div>
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1">Tipo</label>
            <select value={form.absence_type} onChange={e => setForm({ ...form, absence_type: e.target.value })}
              className="w-full h-10 px-3 bg-zinc-700 border border-zinc-600 rounded-sm text-sm text-white" data-testid="driver-abs-type">
              <option value="ferie">Ferie</option><option value="malattia">Malattia</option>
              <option value="permesso">Permesso</option><option value="altro">Altro</option>
            </select>
          </div>
          <div className="flex items-center gap-3">
            <label className="flex items-center gap-2 cursor-pointer" data-testid="driver-abs-partial-toggle">
              <div className={`relative w-10 h-5 rounded-full transition-colors ${form.partial ? "bg-[#FF5200]" : "bg-zinc-600"}`}
                onClick={() => setForm({ ...form, partial: !form.partial, date_to: !form.partial ? form.date_from : form.date_to })}>
                <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${form.partial ? "translate-x-5" : ""}`} />
              </div>
              <span className="text-xs text-zinc-400">Solo alcune ore</span>
            </label>
            {form.partial && (
              <div className="flex items-center gap-2">
                <input type="time" value={form.time_from} onChange={e => setForm({ ...form, time_from: e.target.value })}
                  className="h-9 px-2 bg-zinc-700 border border-zinc-600 rounded-sm text-xs text-white w-24" data-testid="driver-abs-time-from" />
                <span className="text-zinc-500">&mdash;</span>
                <input type="time" value={form.time_to} onChange={e => setForm({ ...form, time_to: e.target.value })}
                  className="h-9 px-2 bg-zinc-700 border border-zinc-600 rounded-sm text-xs text-white w-24" data-testid="driver-abs-time-to" />
              </div>
            )}
          </div>
          <div>
            <label className="block text-[10px] font-bold uppercase text-zinc-500 mb-1">Note</label>
            <input value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} placeholder="Motivo della richiesta..."
              className="w-full h-10 px-3 bg-zinc-700 border border-zinc-600 rounded-sm text-sm text-white" />
          </div>
          <button type="submit" disabled={saving} data-testid="driver-abs-submit"
            className="w-full h-11 bg-[#FF5200] text-white text-sm font-bold uppercase rounded-sm disabled:opacity-50 flex items-center justify-center gap-2">
            {saving ? <Loader2 size={16} className="animate-spin" /> : <CalendarPlus size={16} />} Invia Richiesta
          </button>
        </form>
      )}

      {absences.length === 0 ? (
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <CalendarX size={40} className="text-zinc-700 mx-auto mb-3" />
            <p className="text-sm text-zinc-500">Nessuna assenza registrata</p>
            <p className="text-xs text-zinc-600 mt-1">Clicca &quot;Nuova Richiesta&quot; per richiedere un permesso</p>
          </div>
        </div>
      ) : (
        <div className="flex-1 space-y-2 overflow-y-auto">
          {absences.map(a => (
            <div key={a.id} className="bg-zinc-800/80 border border-zinc-700/50 rounded-sm px-4 py-3 flex items-center gap-3" data-testid={`driver-abs-row-${a.id}`}>
              <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${statusC[a.status] || "bg-zinc-500"}`} />
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-semibold ${typeC[a.absence_type] || "text-zinc-300"}`}>
                  {typeL[a.absence_type] || a.absence_type}{a.partial ? " (parziale)" : ""}
                </p>
                <p className="text-xs text-zinc-500 mt-0.5">
                  {a.date_from?.split("-").reverse().join("/")} &mdash; {a.date_to?.split("-").reverse().join("/")}
                  {a.partial && a.time_from && a.time_to ? ` | ${a.time_from}-${a.time_to}` : ""}
                </p>
                {a.note && <p className="text-[10px] text-zinc-600 mt-0.5 truncate">{a.note}</p>}
              </div>
              <span className={`px-2 py-1 text-[10px] font-bold uppercase rounded-sm ${
                a.status === "approvato" ? "bg-green-500/20 text-green-400" : a.status === "in_attesa" ? "bg-amber-500/20 text-amber-400" : "bg-red-500/20 text-red-400"
              }`}>{statusL[a.status]}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
